<template>
  <div>
    <v-row class="px-2">
      <v-col cols="7" class="pb-0">
        <div>
          <div class="mb-2">
            <AppShowDisciplineName :name="event.discipline.name" chip />
            <AppShowEventStatus
              :event="event"
              no-button
              always-show
              variant="outlined"
            />
          </div>
          <div>
            <div class="event-card--locations">
              {{ state.eventDate }}
              <span class="line" />
              <AppShowCountry :country="event.country">
                <template #append
                  >, {{ state.regionName }},
                  {{ event.place }}
                </template>
              </AppShowCountry>
            </div>
            <div class="event-card--title">
              <b> <AppShowEventName :event="event" disable-link /></b>
              <v-icon
                v-if="event.isHighlighted"
                style="font-size: 24px"
                color="star"
              >
                $iconStar
              </v-icon>
            </div>
            <div class="event-card--organiser">
              <AppOrganisationNameLink :organisation="event.organiser" />
              <template v-if="event.hostOrganisation">
                |
                <AppOrganisationNameLink
                  :organisation="event.hostOrganisation"
                />
              </template>
            </div>
          </div>

          <div class="event-card--description">
            <div v-html="event.description" />
          </div>
        </div>

        <div class="event-card--misc text-secondary">
          <div style="flex-wrap: wrap">
            <slot name="event-additional-details">
              <AppShowCompetitionLevelNames :value="event.competitionLevels" />

              <span v-show="event.competitions.length > 0" class="oval" />

              <AppShowDifficultyLevels
                :value="
                  state.eventCompetitions
                    ? state.eventCompetitions.map(c => c.difficultyLevel)
                    : null
                "
                range
              />
              <span v-show="state.eventcodeNames.length > 0" class="oval" />

              <AppShowEventcodes :value="state.eventCompetitions" />
            </slot>
          </div>
          <AppShowEventCupComponent :event="event" />
        </div>
      </v-col>

      <v-col
        cols="5"
        class="pb-0 d-flex justify-end"
        style="position: relative"
      >
        <div>
          <div class="text-center mb-2">
            <AppShowEventReferenceCode :event="event" />
          </div>
          <div class="event-card--logo justify-center">
            <OrganizationLogo :organiser="event.organiser" event />
          </div>
          <div class="event-card--register">
            <AppShowRegistrationBtn
              v-if="event.status == 'registration_open'"
              :event="event"
            />
          </div>
        </div>
      </v-col>
    </v-row>

    <div>
      <slot name="icon-links" />
    </div>

    <div class="event-card--buttons">
      <div v-if="state.canEditEvent">
        <v-tooltip location="bottom">
          <template #activator="{ props: tooltipProps }">
            <v-btn
              v-bind="tooltipProps"
              data-test="actions"
              color="edit-green"
              class="fixed-v-btn mb-2 text-white"
              @click="() => $emit('click:settings')"
            >
              <v-icon
                v-if="event.notes && event.notes.length"
                color="white"
                start
              >
                $iconCommentDots
              </v-icon>
              {{
                $t(
                  "events:EventCardLarge.Actions.Actions.button",
                  "Actions",
                  "Actions modal button in event page"
                )
              }}
            </v-btn>
          </template>
          <span style="max-width: 5rem">
            {{
              $t(
                "events:EventCardLarge.Actions.Actions.tooltip",
                "Actions and settings, incl edit, change log, changing event status",
                "Tooltip for actions modal button in event page"
              )
            }}
          </span>
        </v-tooltip>
      </div>

      <div v-if="state.canEnterDashboard">
        <v-tooltip location="bottom">
          <template #activator="{ props: tooltipProps }">
            <v-btn
              v-bind="tooltipProps"
              data-test="dashboard"
              color="rose"
              :to="{
                name: pages.dashboard,
                params: { eventId: event.id }
              }"
              class="fixed-v-btn mb-2"
            >
              {{ $t("events:dashboard.button", "Dashboard") }}
            </v-btn>
          </template>
          <span>{{
            $t(
              "events:EventCardLarge.Actions.dashboard.tooltip",
              "Statistics on entries and products, manage competitions´ and products´ settings"
            )
          }}</span>
        </v-tooltip>
      </div>

      <div v-if="state.canEnterMasterlist" class="text-center">
        <v-tooltip location="bottom">
          <template #activator="{ props: tooltipProps }">
            <v-btn
              v-bind="tooltipProps"
              :to="{
                name: pages.masterlist,
                params: { eventId: event.id }
              }"
              class="fixed-v-btn mb-2"
              data-test="masterlist"
              color="edit"
            >
              {{
                $t(
                  "events:EventCardLarge.Actions.Masterlist.button",
                  "Masterlist",
                  "Masterlist button in event page"
                )
              }}
            </v-btn>
          </template>
          <span>
            {{
              $t(
                "events:EventCardLarge.Actions.Masterlist.tooltip",
                "Full masterlist for organisers",
                "Tooltip for Masterlist button in event page"
              )
            }}
          </span>
        </v-tooltip>
      </div>

      <div v-if="state.canViewProductsMasterlist" class="text-center">
        <v-tooltip location="bottom">
          <template #activator="{ props: tooltipProps }">
            <v-btn
              v-bind="tooltipProps"
              :to="{
                name: pages.productsMasterlsit,
                params: { eventId: event.id }
              }"
              class="fixed-v-btn elevation-1 mb-2"
              variant="outlined"
              color="#357133"
              data-test="products"
            >
              {{
                $t(
                  "events:EventCardLarge.Actions.Products.button",
                  "Products",
                  "Products button in event page"
                )
              }}
            </v-btn>
          </template>
          <span>
            {{
              $t(
                "events:EventCardLarge.Actions.Products.tooltip",
                "Products",
                "Tooltip for Products button in event page"
              )
            }}
          </span>
        </v-tooltip>
      </div>
      <div v-if="state.canViewReports" class="text-center">
        <v-tooltip location="bottom">
          <template #activator="{ props: tooltipProps }">
            <v-btn
              v-bind="tooltipProps"
              :to="{
                name: 'EventSurveyStats',
                params: { eventId: event.id }
              }"
              class="fixed-v-btn elevation-1 mb-2"
              variant="outlined"
              color="#013941"
              data-test="reports"
            >
              {{
                $t("events:EventCardLarge.Actions.Surveys.button", "Reports")
              }}
            </v-btn>
          </template>
          <span>{{
            $t("events:EventCardLarge.Actions.surveys.tooltip", "Event reports")
          }}</span>
        </v-tooltip>
      </div>

      <div v-if="state.canUsePayments">
        <v-tooltip location="bottom">
          <template #activator="{ props: tooltipProps }">
            <v-btn
              v-bind="tooltipProps"
              :to="{
                name: pages.finances,
                params: { eventId: event.id }
              }"
              data-test="finances"
              class="fixed-v-btn elevation-1 mb-2"
              variant="outlined"
              color="#208297"
            >
              {{
                $t("events:EventCardLarge.Actions.Finances.button", "Finances")
              }}
            </v-btn>
          </template>
          <span>{{
            $t(
              "events:EventCardLarge.Actions.finances.tooltip",
              "Overview of revenues and other financial information regarding online payments"
            )
          }}</span>
        </v-tooltip>
      </div>
      <div v-if="state.canUseEquipe" class="text-center">
        <AppEquipeBtn
          :equipe-auth-data="event.equipeAuthData"
          :disabled="state.equipeDisabled"
          data-test="equipe"
        />
      </div>
    </div>

    <br />
    <AppShowRegistrationDeadline :event="event" align-text="left" />
  </div>
</template>
<script>
export default {
  name: "EventCardLargeDesktop"
};
</script>
<script setup>
import { inject } from "vue";
import AppShowCountry from "@AppShows/AppShowCountry.vue";
import OrganizationLogo from "@/components/elements/AppOrganLogo.vue";
import AppShowEventCupComponent from "@/components/elements/AppShows/event/AppShowEventCupComponent.vue";
import AppShowCompetitionLevelNames from "@/components/elements/AppShows/event/AppShowCompetitionLevelNames.vue";
import AppShowDisciplineName from "@/components/elements/AppShows/AppShowDisciplineName.vue";
import AppShowEventStatus from "@/components/elements/AppShows/event/AppShowEventStatus.vue";
import AppShowEventcodes from "@/components/elements/AppShows/event/AppShowEventcodes.vue";
import AppOrganisationNameLink from "@/components/elements/AppNameLinks/AppOrganisationNameLink.vue";
import AppShowRegistrationBtn from "@/components/elements/AppShows/event/AppShowRegistrationBtn.vue";
import AppShowDifficultyLevels from "@/components/elements/AppShows/AppShowDifficultyLevels.vue";
import AppEquipeBtn from "@/components/elements/AppEquipeBtn.vue";
import AppShowEventReferenceCode from "@AppShows/event/AppShowEventReferenceCode.vue";

import AppShowEventName from "@AppShows/event/AppShowEventName.vue";
import AppShowRegistrationDeadline from "@AppShows/event/AppShowRegistrationDeadline.vue";

defineProps({
  event: {
    type: Object,
    required: true
  },
  state: {
    type: Object,
    required: true
  }
});
defineEmits(["click:settings"]);

const isNoncompetition = inject("isNoncompetition", false);

const pages = {
  dashboard: isNoncompetition
    ? "NoncompetitionEventDashboard"
    : "EventDashboard",
  masterlist: isNoncompetition
    ? "NoncompetitionEventMasterlistAdminPage"
    : "EventMasterlistAdminPage",
  productsMasterlsit: isNoncompetition
    ? "NoncompetitionEventMasterlistProductsPage"
    : "EventMasterlistProductsPage",
  finances: isNoncompetition
    ? "NoncompetitionEventFinancesPage"
    : "EventFinancesPage"
};
</script>
<style lang="scss">
.equipe-btn {
  border: 1px solid rgb(var(--v-theme-edit));
}
.finances-btn {
  border: 1px solid rgb(var(--v-theme-primary));
}

.products-btn {
  border: 1px solid green;
}

.event-card {
  &--locations {
    font-size: 18px;
    .line {
      margin: 0 10px;
      border: 1px solid #9b9b9b;
      background: #9b9b9b;
      width: 1px;
    }
  }
  &--title {
    font-size: 21px;
    margin-top: 30px;
    display: flex;
    @media (min-width: 1264px) {
      b {
        flex-shrink: 0;
        max-width: 764px;
      }
    }

    i {
      margin-left: 4px;
    }
  }

  &--organiser {
    text-transform: uppercase;
    font-size: 16px;
  }

  &--description {
    margin-top: 20px;
    font-size: 16px;
    p {
      margin: 0 !important;
      font-size: 16px;
    }
  }

  &--misc {
    margin-top: 20px;
    font-size: 14px;
    & > div {
      height: 100%;
      display: flex;
      align-content: center;
      align-items: flex-end;
      .oval {
        margin: 8px;
        width: 5px;
        height: 5px;
        background: grey;
        border-radius: 20px;
      }
    }
  }

  &--buttons {
    margin-top: 20px;
    display: flex;
    flex-direction: row;
    & > div {
      margin-left: 14px;
      &:first-child {
        margin-left: 0;
      }
    }
    .v-btn {
      min-width: 160px !important;
    }
  }

  &--logo {
    display: flex;
    justify-content: flex-end;

    img {
      max-width: 160px;
      max-height: 160px;
      width: auto !important;
    }
  }

  &--register {
    margin-top: 20px;
    display: flex;
    justify-content: flex-end;
  }
}
</style>
<style lang="scss" scoped>
.textred {
  color: #771e24;
}

.registerbtn {
  border-radius: 3px;
}
.current-status {
  font-weight: bold;
  font-size: 1.2em;
}
a[href]:hover:not(.v-btn),
a[href]:focus:not(.v-btn) {
  color: black !important;
  text-decoration: none;
}
.mobile-event-btns {
  display: flex;
  flex-flow: column;
  justify-items: center;
  & > div {
    margin: auto;

    button,
    a {
      width: 25em;
    }
  }
}
</style>
